import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

interface CTAButton {
    id: string;
    name: string;
    url: string;
    type: 'SRP' | 'FORM';
    tags?: string[]; // Array of tag IDs or names
}

interface Contact {
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    first_name?: string;
    last_name?: string;
    title?: string;
    name?: string;
}

interface DaysOfService {
    status?: boolean;
    start_time?: number;
    end_time?: number;
}

interface MarketingData {
    ideal_customer?: {
        household_income?: number;
        purchasing_power?: number;
    };
    adf?: {
        lead_expiry?: number;
        incoming_mailbox?: string;
        outgoing_mailbox?: string;
        crm_email?: string;
    };
    nurturing?: {
        status?: boolean;
        nurturing_cycle?: number;
        long?: boolean;
        immediate?: boolean;
        test_mode?: boolean;
        nurture_exist_lead?: boolean;
    };
    conquest?: {
        source?: string;
        conquest_per_month?: number;
    };
    scan?: {
        source?: string;
        scans_per_month?: number;
    };
    engine_mode?: string;
}

interface DealerContentSetting {
    _id?: string; // MongoDB document ID
    dealer: string; // changed from dealer_id
    // Basic Settings
    primary_contact?: Contact;
    invoice_contact?: Contact;
    secondary_contact?: Contact;
    days_of_service?: {
        sunday?: DaysOfService;
        monday?: DaysOfService;
        tuesday?: DaysOfService;
        wednesday?: DaysOfService;
        thursday?: DaysOfService;
        friday?: DaysOfService;
        saturday?: DaysOfService;
    };
    make?: string[]; // Array of Make ObjectIds
    manufacturer?: string | null; // Legacy manufacturer ObjectId (make-centric UI clears this)
    marketing_data?: MarketingData;
    time_zone?: string;
    logo_bar_url: {
        title: string;
        value: string;
        type: string;
    };
    address: {
        title: string;
        value: string;
        type: string;
    };
    phone: {
        title: string;
        value: string;
        type: string;
    };
    standard_disclaimer_text: {
        title: string;
        value: string;
        type: string;
    };
    footer_url: {
        title: string;
        value: string;
        type: string;
    };
    footer_image_url: {
        title: string;
        value: string;
        type: string;
    };
    cta_buttons: CTAButton[];
    content_font: {
        title: string;
        value: string;
        type: string;
    };
    content_font_color: {
        title: string;
        value: string;
        type: string;
    };
    content_font_size: {
        title: string;
        value: string;
        type: string;
    };
    primary_headline_font_color: {
        title: string;
        value: string;
        type: string;
    };
    primary_headline_size: {
        title: string;
        value: string;
        type: string;
    };
    headline_highlight_font_color: {
        title: string;
        value: string;
        type: string;
    };
    headline_highlight_font_size: {
        title: string;
        value: string;
        type: string;
    };
    disclaimer_font: {
        title: string;
        value: string;
        type: string;
    };
    disclaimer_font_color: {
        title: string;
        value: string;
        type: string;
    };
    disclaimer_font_size: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_background_color: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_border_size: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_border_color: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_font: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_font_color: {
        title: string;
        value: string;
        type: string;
    };
    primary_cta_button_font_size: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_background_color: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_border_size: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_border_color: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_font: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_font_color: {
        title: string;
        value: string;
        type: string;
    };
    secondary_cta_button_font_size: {
        title: string;
        value: string;
        type: string;
    };
    created_at?: Date;
    updated_at?: Date;
    created_by?: string;
    updated_by?: string;
}

class DealerContentSettings {
    static ENDPOINT = 'api/v2/dealer-content-setting';

    static async getDealerContentSettings(dealer: string): Promise<APIResponseType> {
        if (!dealer) throw new Error("Dealer ID is required to fetch content settings");
        try {
            // Use dealer as query param
            const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}?dealer=${dealer}`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async createDealerContentSettings(payload: Partial<DealerContentSetting>): Promise<APIResponseType> {
        if (!payload.dealer) throw new Error("Dealer ID is required to create content settings");
        try {
            // Use dealer in payload
            const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async updateDealerContentSettings(id: string, payload: Partial<DealerContentSetting>): Promise<APIResponseType> {
        if (!id) throw new Error("ID is required to update content settings");
        if (!Object.keys(payload).length) throw new Error("Update payload cannot be empty");
        try {
            // Use _id as path parameter
            const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${id}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async deleteDealerContentSettings(dealer: string): Promise<APIResponseType> {
        if (!dealer) throw new Error("Dealer ID is required to delete content settings");
        try {
            // Use dealer as query param
            const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}?dealer=${dealer}`);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }
}

export default DealerContentSettings;
export type { DealerContentSetting, CTAButton, Contact, DaysOfService, MarketingData }; 